"use client";

import { useMemo, useState } from "react";
import Link from "next/link";

export interface MyTicketRow {
  id: string;
  ticketNumber: number;
  subject: string;
  status: string;
  category: string;
  customCategory?: string | null;
  priority: string;
  createdAt: string;
  /** Optional — when set, used for "last activity" sort + label. */
  updatedAt?: string;
  /** Admin-set resolve-by date. Read-only on submitter views. */
  dueAt?: string | null;
}

interface SupportMyTicketsListProps {
  tickets: MyTicketRow[];
  /** Base path for ticket detail links. Defaults to "/dashboard/support/tickets". */
  basePath?: string;
  /** Path for the "Submit a new ticket" CTA. Defaults to "/dashboard/support". */
  newTicketPath?: string;
}

const STATUS_META: Record<
  string,
  { label: string; tint: string }
> = {
  OPEN: { label: "Open", tint: "bg-blue-50 text-blue-800 ring-blue-200" },
  IN_PROGRESS: { label: "In progress", tint: "bg-amber-50 text-amber-800 ring-amber-200" },
  WAITING_ON_USER: { label: "Waiting on you", tint: "bg-purple-50 text-purple-800 ring-purple-200" },
  RESOLVED: { label: "Resolved", tint: "bg-emerald-50 text-emerald-800 ring-emerald-200" },
  CLOSED: { label: "Closed", tint: "bg-gray-100 text-gray-700 ring-gray-200" },
};

const PRIORITY_DOT: Record<string, string> = {
  LOW: "bg-emerald-400",
  MEDIUM: "bg-amber-400",
  HIGH: "bg-orange-500",
  URGENT: "bg-red-500",
};

const CATEGORY_LABEL: Record<string, string> = {
  ACCOUNT: "Account",
  BILLING: "Billing",
  BOOKING: "Booking",
  TECHNICAL: "Technical",
  QUESTION: "Question",
  FEEDBACK: "Feedback",
  OTHER: "Other",
};

// Postgres `timestamp without time zone` returns naive ISO strings — append
// `Z` so JS parses them as UTC, then `toLocaleString` shows the viewer's
// browser timezone. Same gotcha that's documented in
// `project_naive_timestamp_columns` memory.
function asDate(iso: string): Date {
  const hasOffset = /Z$|[+-]\d{2}:?\d{2}$/.test(iso);
  return new Date(hasOffset ? iso : `${iso}Z`);
}

function relativeFromNow(iso: string): string {
  const then = asDate(iso).getTime();
  const diff = Date.now() - then;
  if (diff < 0) return "just now";
  const m = Math.floor(diff / 60_000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}

type StatusFilter = "ALL" | "OPEN" | "WAITING" | "RESOLVED";

export function SupportMyTicketsList({
  tickets,
  basePath = "/dashboard/support/tickets",
  newTicketPath = "/dashboard/support",
}: SupportMyTicketsListProps) {
  const [filter, setFilter] = useState<StatusFilter>("ALL");
  const [search, setSearch] = useState("");

  const counts = useMemo(() => {
    let open = 0;
    let waiting = 0;
    let resolved = 0;
    for (const t of tickets) {
      if (t.status === "RESOLVED" || t.status === "CLOSED") resolved += 1;
      else if (t.status === "WAITING_ON_USER") waiting += 1;
      else open += 1;
    }
    return { open, waiting, resolved, all: tickets.length };
  }, [tickets]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tickets.filter((t) => {
      if (filter === "OPEN" && !(t.status === "OPEN" || t.status === "IN_PROGRESS")) return false;
      if (filter === "WAITING" && t.status !== "WAITING_ON_USER") return false;
      if (filter === "RESOLVED" && !(t.status === "RESOLVED" || t.status === "CLOSED")) return false;
      if (!q) return true;
      const asNum = Number.parseInt(q.replace(/^#/, ""), 10);
      if (Number.isFinite(asNum) && t.ticketNumber === asNum) return true;
      return (
        t.subject.toLowerCase().includes(q) ||
        (t.customCategory ?? "").toLowerCase().includes(q)
      );
    });
  }, [tickets, filter, search]);

  if (tickets.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center shadow-sm">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-600 text-2xl">
          📨
        </div>
        <h3 className="mt-4 text-base font-semibold text-gray-900">
          You don't have any tickets yet
        </h3>
        <p className="mx-auto mt-1 max-w-md text-sm text-gray-600">
          Got a question, hit a snag, or have feedback? We're here to help —
          submit a ticket and we'll get back to you.
        </p>
        <Link
          href={newTicketPath}
          className="mt-5 inline-flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800"
        >
          <span aria-hidden>+</span>
          Submit your first ticket
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filter + search bar */}
      <div className="rounded-2xl border border-gray-200 bg-white p-3 shadow-sm sm:p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-1">
            <FilterPill
              active={filter === "ALL"}
              onClick={() => setFilter("ALL")}
              label="All"
              count={counts.all}
            />
            <FilterPill
              active={filter === "OPEN"}
              onClick={() => setFilter("OPEN")}
              label="Active"
              count={counts.open}
              tone="blue"
            />
            <FilterPill
              active={filter === "WAITING"}
              onClick={() => setFilter("WAITING")}
              label="Waiting on you"
              count={counts.waiting}
              tone="purple"
              highlight={counts.waiting > 0}
            />
            <FilterPill
              active={filter === "RESOLVED"}
              onClick={() => setFilter("RESOLVED")}
              label="Resolved"
              count={counts.resolved}
              tone="emerald"
            />
          </div>
          <div className="relative w-full sm:w-64">
            <span
              aria-hidden
              className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400"
            >
              🔎
            </span>
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search subject or #number"
              className="w-full rounded-lg border border-gray-300 bg-white py-1.5 pl-7 pr-3 text-xs shadow-sm focus:border-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-100"
            />
          </div>
        </div>
      </div>

      {/* Tickets list */}
      {visible.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-10 text-center text-sm text-gray-500 shadow-sm">
          No tickets match.{" "}
          <button
            type="button"
            onClick={() => {
              setFilter("ALL");
              setSearch("");
            }}
            className="font-medium text-slate-700 hover:underline"
          >
            Clear filters
          </button>
        </div>
      ) : (
        <ul className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
          {visible.map((t, i) => {
            const sm = STATUS_META[t.status] ?? STATUS_META.OPEN!;
            const isWaiting = t.status === "WAITING_ON_USER";
            const isResolved = t.status === "RESOLVED" || t.status === "CLOSED";
            const lastIso = t.updatedAt ?? t.createdAt;
            return (
              <li key={t.id} className={i > 0 ? "border-t border-gray-100" : ""}>
                <Link
                  href={`${basePath}/${t.id}`}
                  className={`group flex items-start gap-3 px-4 py-3 transition hover:bg-gray-50 sm:px-5 ${
                    isWaiting ? "bg-purple-50/40" : ""
                  }`}
                >
                  <span
                    className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                      PRIORITY_DOT[t.priority] ?? "bg-gray-400"
                    }`}
                    title={`Priority: ${t.priority}`}
                    aria-label={`priority ${t.priority}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span
                        className={`truncate text-sm font-semibold ${
                          isResolved ? "text-gray-600" : "text-gray-900"
                        } group-hover:text-slate-900`}
                      >
                        {t.subject}
                      </span>
                      {t.dueAt && (() => {
                        const isOverdue =
                          !isResolved &&
                          asDate(t.dueAt).getTime() < Date.now();
                        return (
                          <span
                            className={`inline-flex h-4 items-center gap-0.5 rounded-full px-1.5 text-[9px] font-semibold ring-1 ${
                              isOverdue
                                ? "bg-red-100 text-red-700 ring-red-200"
                                : "bg-indigo-50 text-indigo-800 ring-indigo-200"
                            }`}
                            title={
                              isOverdue
                                ? "Past the resolve-by date"
                                : "Resolve by"
                            }
                          >
                            <span aria-hidden>📅</span>
                            {isOverdue ? "Overdue · " : "Due "}
                            {new Intl.DateTimeFormat("en", {
                              month: "short",
                              day: "numeric",
                            }).format(asDate(t.dueAt))}
                          </span>
                        );
                      })()}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-gray-500">
                      <span className="font-mono">#{t.ticketNumber}</span>
                      <span aria-hidden>·</span>
                      <span>
                        {CATEGORY_LABEL[t.category] ?? t.category}
                        {t.category === "OTHER" && t.customCategory
                          ? ` — ${t.customCategory}`
                          : ""}
                      </span>
                      <span aria-hidden>·</span>
                      <span>Updated {relativeFromNow(lastIso)}</span>
                    </div>
                  </div>
                  <span
                    className={`shrink-0 self-center rounded-full px-2 py-0.5 text-[10px] font-semibold ring-1 ${sm.tint}`}
                  >
                    {isWaiting ? "Waiting on you" : sm.label}
                  </span>
                  <span
                    aria-hidden
                    className="shrink-0 self-center text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-gray-500"
                  >
                    ›
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function FilterPill({
  active,
  onClick,
  label,
  count,
  tone = "slate",
  highlight = false,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  tone?: "slate" | "blue" | "purple" | "emerald";
  highlight?: boolean;
}) {
  const activeBg =
    tone === "blue"
      ? "bg-blue-600"
      : tone === "purple"
        ? "bg-purple-600"
        : tone === "emerald"
          ? "bg-emerald-600"
          : "bg-slate-900";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition ${
        active
          ? `${activeBg} text-white shadow-sm`
          : highlight
            ? "bg-purple-50 text-purple-800 ring-1 ring-purple-200 hover:bg-purple-100"
            : "bg-gray-100 text-gray-700 hover:bg-gray-200"
      }`}
    >
      {label}
      <span
        className={`inline-flex items-center justify-center rounded-full px-1.5 text-[10px] font-semibold ${
          active
            ? "bg-white/20 text-white"
            : highlight
              ? "bg-purple-200/70 text-purple-900"
              : "bg-white text-gray-700 ring-1 ring-gray-200"
        }`}
      >
        {count}
      </span>
    </button>
  );
}
